import { createContext, useContext, useMemo } from "react";

/**
 * Props of DataAnalytics Component Context
 */
interface Props {
  /**
   * Component Name.
   *
   * ## Note:
   * By default, if you specify this it will override the parent context component name.
   * If you want to append this to parent context component name use `appendCOmponent`.
   */
  component?: string;

  /**
   * When set to `true`, if you provide multiple layers of the context
   * `component` will be appended to parent layer component name with ` - ` between.
   * e.g. `parentComponent - component`
   */
  appendComponent?: boolean;

  /**
   * Custom properties to be sent to DataAnalytics.
   */
  customProps?: { [key: string]: Record<string, unknown> | number | string };
}

/**
 * DataAnalytics Context to use inside components
 * @type {React.Context<Props>}
 * */
const DataAnalyticsComponentContext = createContext<Props>({});

/**
 * DataAnalytics Component Context can be used inside components to
 * provide component name and custom properties to be sent to DataAnalytics.
 * This context accumulates custom props from all matching parent contexts.
 *
 * ## Usage:
 *
 * ```tsx
 * <DataAnalyticsComponentProvider component="MyComponent">
 *   // ...
 *   // In another component:
 *   <DataAnalyticsComponentProvider customProps={{ myProp: "value" }}>
 *     <MyComponent /> // Assuming that this component will call useAnalytics to consume the context
 *   </DataAnalyticsComponentProvider>
 * </DataAnalyticsComponentProvider>
 * ```
 *
 * @type {React.Context<Props>}
 * */
const DataAnalyticsComponentProvider: React.FC<React.PropsWithChildren<Props>> = ({
  component,
  appendComponent,
  customProps,
  children,
}) => {
  const parentContext = useContext(DataAnalyticsComponentContext);
  const combinedComponent =
    appendComponent && component && parentContext.component
      ? `${parentContext.component} - ${component}`
      : component || parentContext.component;

  const combinedContextValue = useMemo(
    () => ({
      component: combinedComponent,
      customProps: {
        ...parentContext.customProps,
        ...customProps,
      },
    }),
    [combinedComponent, customProps, parentContext.customProps]
  );

  return (
    <DataAnalyticsComponentContext.Provider value={combinedContextValue}>
      {children}
    </DataAnalyticsComponentContext.Provider>
  );
};

export { DataAnalyticsComponentContext, DataAnalyticsComponentProvider };
